import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, CardBody, CardHeader, Button } from 'reactstrap';
import TableContainer from '../../../Components/Common/TableContainer';
import RemissionGuideDetailModal from './Components/RemissionGuideDetailModal';
import SriErrorModal from './Components/SriErrorModal';
import CopyConfirmModal from '../../../Components/Common/CopyConfirmModal';
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { useDispatch, useSelector, TypedUseSelectorHook } from 'react-redux';
import { getWaybills, retryWaybill } from '../../../slices/waybills/thunk';
import { RootState } from '../../../slices';
import { configureStore } from '@reduxjs/toolkit';
import rootReducer from '../../../slices';
import {toast} from "react-toastify";
import {getWaybillFile} from "../../../slices/waybills/api";
import PdfViewerModal from "../../../Components/PdfViewerModal/PdfViewerModal";
import { useNavigate } from 'react-router-dom';
import { debounce } from 'lodash';

const store = configureStore({ reducer: rootReducer, devTools: true });
type AppDispatch = typeof store.dispatch;

export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

const RemissionGuideList = () => {
    document.title = "Lista de Guías de Remisión | Vuelo";


    const dispatch = useAppDispatch();
    const { waybillList, isLoading, pagination } = useAppSelector((state) => state.Waybills);
    const navigate = useNavigate();
    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [pdfUrl, setPdfUrl] = useState('');
    const [pdfModalOpen, setPdfModalOpen] = useState(false);

    const [errorModalOpen, setErrorModalOpen] = useState(false);
    const [currentErrorStatus, setCurrentErrorStatus] = useState('');
    const [currentErrors, setCurrentErrors] = useState<Array<{type: string; message: string; additionalInfo?: string}>>([]);

    const [copyConfirmOpen, setCopyConfirmOpen] = useState<boolean>(false);
    const [guideToCopy, setGuideToCopy] = useState<any>(null);

    const [statusFilter, setStatusFilter] = useState('');
    const [dateFilter, setDateFilter] = useState('');
    const [retryingIds, setRetryingIds] = useState<string[]>([]);
    const [optimisticEnqueuedIds, setOptimisticEnqueuedIds] = useState<string[]>([]);

    const handleRetry = useCallback(async (rowData: { id: string; progressJobId: string; progressStatus: string; status: string; sriMessage: any }) => {
        const { id, progressJobId, progressStatus, status, sriMessage } = rowData;

        // Determinar el progressStatus válido para la cola correspondiente
        let validProgressStatus = progressStatus;
        const validStatuses = ['ENQUEUED', 'CREATED_XML', 'SIGNED', 'SENT_SRI'];
        
        if (!validStatuses.includes(progressStatus)) {
            if (status === 'REJECTED' || status === 'DEVUELTA' || status === 'UNAUTHORIZED' || (Array.isArray(sriMessage) && sriMessage.length > 0)) {
                validProgressStatus = 'SENT_SRI';
            } else {
                validProgressStatus = 'ENQUEUED';
            }
        }

        setRetryingIds(prev => [...prev, id]);

        try {
            const response: any = await dispatch(retryWaybill({ progressJobId, progressStatus: validProgressStatus })).unwrap();

            if (response?.success) {
                setOptimisticEnqueuedIds(prev => [...prev, id]);
            }

            setTimeout(() => {
                dispatch(getWaybills({
                    page: currentPage,
                    limit: pageSize,
                    search: searchTerm,
                    status: statusFilter,
                    issueDate: dateFilter,
                }));
                setOptimisticEnqueuedIds(prev => prev.filter(enqId => enqId !== id));
                setRetryingIds(prev => prev.filter(retryId => retryId !== id));
            }, 5000);

        } catch (error) {
            console.error(error);
            setRetryingIds(prev => prev.filter(retryId => retryId !== id));
        }
    }, [dispatch, currentPage, pageSize, searchTerm, statusFilter, dateFilter]);

    const copyToClipboard = async (text: string) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success('¡Copiado al portapapeles!');
        } catch (err) {
            console.error('Error al copiar:', err);
            toast.error('Error al copiar');
        }
    };

    useEffect(() => {
        dispatch(getWaybills({
            page: currentPage,
            limit: pageSize,
            search: searchTerm,
            status: statusFilter,
            issueDate: dateFilter,
        }));
    }, [dispatch, currentPage, pageSize, searchTerm, statusFilter, dateFilter]);

    useEffect(() => {
        const pendingStatuses = ['DRAFT', 'INPROGRESS'];
        const hasPending = waybillList?.some((wb: any) =>
            pendingStatuses.includes(wb.status) || retryingIds.includes(wb.id)
        );

        let interval: NodeJS.Timeout;

        if (hasPending) {
            interval = setInterval(() => {
                dispatch(getWaybills({
                    page: currentPage,
                    limit: pageSize,
                    search: searchTerm,
                    status: statusFilter,
                    issueDate: dateFilter,
                }));
            }, 5000);
        }

        return () => {
            if (interval) clearInterval(interval);
        };
    }, [waybillList, dispatch, currentPage, pageSize, searchTerm, statusFilter, dateFilter, retryingIds, handleRetry, optimisticEnqueuedIds]);

    const debouncedSearch = useMemo(() =>
        debounce((value: string) => {
            setCurrentPage(1);
            setSearchTerm(value);
        }, 500)
    , []);

    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);

    const statusOptions = [
        {value: '', label: 'Todos los estados'},
        {value: 'AUTHORIZED', label: 'AUTORIZADO'},
        {value: 'UNAUTHORIZED', label: 'NO AUTORIZADO'},
        {value: 'REJECTED', label: 'DEVUELTA'},
        {value: 'ACCEPTED', label: 'RECIBIDA'},
        {value: 'INPROGRESS', label: 'EN PROCESO'},
        {value: 'DRAFT', label: 'BORRADOR'},
        {value: 'ERROR', label: 'ERROR'}
    ];

    const statusFilterComponent = (
        <select
            className="form-select"
            value={statusFilter}
            onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
            }}
        >
            {statusOptions.map(option => (
                <option key={option.value} value={option.value}>
                    {option.label}
                </option>
            ))}
        </select>
    );

    const dateFilterComponent = (
        <input
            type="date"
            className="form-control"
            value={dateFilter}
            onChange={(e) => {
                setDateFilter(e.target.value);
                setCurrentPage(1);
            }}
            placeholder="Fecha Emisión"
        />
    );


    const handleDownload = async (
        extension: 'pdf' | 'xml',
        accessKey: string,
    ) => {
        try {
            const response: any = await getWaybillFile(accessKey, extension);

            {
                const fileInstance = document.createElement('a');
                const href = response?.url;
                if (!href) throw new Error('PDF url missing');
                fileInstance.href = href;
                fileInstance.target = '_blank';
                document.body.appendChild(fileInstance);
                fileInstance.click();
                document.body.removeChild(fileInstance);
            }
        } catch (err) {
            toast.error(`No se pudo descargar el archivo ${extension.toUpperCase()}.`);
        }
    };

    const handleViewPdf = async (accessKey: string) => {
        try {
            const response: any = await getWaybillFile(accessKey, 'pdf');
            setPdfUrl(response.url);
            setPdfModalOpen(true);
        } catch (err) {
            toast.error("No se pudo cargar el archivo PDF.");
        }
    };

    const handleShowErrors = (status: string, errors: Array<{type: string; message: string; additionalInfo?: string}>) => {
        setCurrentErrorStatus(status);
        setCurrentErrors(errors);
        setErrorModalOpen(true);
    };

    const handleCopyGuide = useCallback((guide: any) => {
        try {
            const guideData = {
                guideInfo: guide.jsonContent || {},
                transporterInfo: guide.jsonContent?.infoGuiaRemision || {},
                waybillInfo: guide.jsonContent?.infoGuiaRemision || {},
                receivers: guide.jsonContent?.destinatarios?.destinatario || [],
                details: guide.jsonContent?.destinatarios?.destinatario?.[0]?.detalles?.detalle || [],
                additionalInfo: guide.jsonContent?.infoAdicional || [],
                taxInfo: guide.jsonContent?.infoTributaria || {}
            };
            
            sessionStorage.setItem('copiedRemissionData', JSON.stringify(guideData));
            navigate('/remission-guide');
        } catch (error) {
            console.error('Error al copiar guía:', error);
            toast.error("No se pudo copiar la guía");
        }
    }, [navigate]);

    const onRequestCopy = useCallback((guide: any) => {
        setGuideToCopy(guide);
        setCopyConfirmOpen(true);
    }, []);

    document.title = 'Lista Guía de Remisión | Vuelo';

    const columns = useMemo(
        () => [
            {
                header: 'Nº Autorización',
                accessorFn: (row: any) => row?.accessKey ?? row?.taxInfo?.accessKey ?? '',
                cell: (cell: any) => {
                    const value = cell.getValue();
                    return (
                        <div className="d-flex align-items-center">
                            <span className="text-truncate" style={{maxWidth: '150px'}} title={value}>
                                {value}
                            </span>
                            <i
                                className="ri-file-copy-line ms-2 text-muted"
                                onClick={() => copyToClipboard(value)}
                                title="Copiar Clave de Acceso"
                                style={{cursor: 'pointer'}}
                            />
                        </div>
                    );
                }
            },
            {
                header: 'Secuencial SRI',
                accessorKey: 'sriSequential',
            },
            {
                header: 'Transportista',
                accessorKey: 'transporterName',
            },
            {
                header: 'Placa',
                accessorKey: 'licensePlate',
            },
            {
                header: 'Fecha Emisión',
                accessorKey: 'issueDate',
            },
            {
                header: 'Estado',
                accessorKey: 'status',
                cell: (cell: any) => {
                    const row = cell.row.original;
                    const isRetrying = retryingIds.includes(row.id);
                    const isOptimisticEnqueued = optimisticEnqueuedIds.includes(row.id);

                    if (isRetrying) {
                        return (
                            <span className="badge bg-info d-inline-flex align-items-center">
                              <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                              REINTENTANDO...
                            </span>
                        );
                    }

                    const value = isOptimisticEnqueued ? 'ENQUEUED' : (cell.getValue() as string);
                    let badgeClass = 'badge bg-secondary';
                    if (value === 'AUTHORIZED') badgeClass = 'badge bg-success';
                    else if (value === 'UNAUTHORIZED') badgeClass = 'badge bg-danger';
                    else if (value === 'ACCEPTED') badgeClass = 'badge bg-success';
                    else if (value === 'REJECTED') badgeClass = 'badge bg-warning';
                    else if (value === 'SIGNED') badgeClass = 'badge bg-primary';
                    else if (value === 'ERROR') badgeClass = 'badge bg-danger';
                    else if (value === 'DRAFT') badgeClass = 'badge bg-secondary';
                    else if (value === 'INPROGRESS') badgeClass = 'badge bg-info';

                    const statusText: { [key: string]: string } = {
                        'AUTHORIZED': 'AUTORIZADO',
                        'UNAUTHORIZED': 'NO AUTORIZADO',
                        'ACCEPTED': 'RECIBIDA',
                        'REJECTED': 'DEVUELTA',
                        'SIGNED': 'FIRMADO',
                        'ERROR': 'ERROR',
                        'DRAFT': 'BORRADOR',
                        'INPROGRESS': 'EN PROCESO'
                    };

                    return <span className={badgeClass}>{statusText[value] || value}</span>;
                },
            },
            {
                header: 'Acciones',
                cell: (cell: any) => {
                    const status = cell?.row?.original?.status;
                    const sriMessages = cell?.row?.original?.sriMessage;
                    const key = cell?.row?.original?.accessKey ?? cell?.row?.original?.taxInfo?.accessKey;
                    const rowData = cell.row.original;

                    const errorStates = ['UNAUTHORIZED', 'ACCEPTED', 'REJECTED', 'ERROR', 'DRAFT'];
                    const hasErrorMessages = sriMessages && sriMessages.length > 0 && errorStates.includes(status);

                    const isRetrying = retryingIds.includes(rowData.id);
                    const isOptimisticEnqueued = optimisticEnqueuedIds.includes(rowData.id);


                    return (
                        <div className="d-flex gap-2 justify-content-center align-items-center">
                            {status === 'AUTHORIZED' && (
                                <>
                                    <Button
                                        color="info"
                                        size="sm"
                                        onClick={() => {
                                            if (key) {
                                                handleViewPdf(key);
                                            } else {
                                                toast.error('Clave de acceso no disponible para esta guía.');
                                            }
                                        }}
                                        title="Ver Detalles"
                                    >
                                        <i className="ri-eye-fill"></i>
                                    </Button>

                                    <Button
                                        onClick={() => {
                                            if (key) {
                                                handleDownload('pdf', key);
                                            } else {
                                                toast.error('Clave de acceso no disponible para esta guía.');
                                            }
                                        }}
                                        color="warning"
                                        size="sm"
                                        title="Descargar PDF"
                                    >
                                        PDF
                                    </Button>

                                    <Button
                                        onClick={() => {
                                            if (key) {
                                                handleDownload('xml', key);
                                            } else {
                                                toast.error('Clave de acceso no disponible para esta guía.');
                                            }
                                        }}
                                        color="secondary"
                                        size="sm"
                                        title="Descargar XML"
                                    >
                                        XML
                                    </Button>
                                </>
                            )}

                            {status !== 'ACCEPTED' && (
                                (
                                    status === 'DRAFT' &&
                                    ['QUEUE_FAILED_TECHNICAL', 'QUEUE_PENDING'].includes(rowData.progressStatus) &&
                                    !isOptimisticEnqueued
                                )
                                ||
                                (
                                    status === 'REJECTED'
                                )
                                ||
                                (
                                    (() => {
                                        try {
                                            let msgs = sriMessages;
                                            if (typeof msgs === 'string') msgs = JSON.parse(msgs);
                                            
                                            let lastAttempt = 0;
                                            if (Array.isArray(msgs) && msgs.length > 0) {
                                                lastAttempt = Number(msgs[msgs.length - 1]?.attempt) || 0;
                                            }

                                            return lastAttempt >= 20;
                                        } catch (e) {
                                            return false;
                                        }
                                    })()
                                )
                            ) && (
                                <Button
                                    color="warning"
                                    size="sm"
                                    onClick={() => handleRetry(rowData)}
                                    disabled={isRetrying}
                                    title="Reintentar"
                                >
                                    <i className="ri-refresh-line"></i>
                                </Button>
                            )}

                            {hasErrorMessages && (
                                <Button
                                    color="danger"
                                    size="sm"
                                    onClick={() => {
                                        const lastMessage = sriMessages[sriMessages.length - 1];
                                        const errors = lastMessage?.messages || [];
                                        const errorDetails = errors.map((err: any) => ({
                                            type: err.type,
                                            message: err.message,
                                            additionalInfo: err.additionalInfo
                                        }));
                                        
                                        handleShowErrors(status, errorDetails);
                                    }}
                                    title="Ver Errores del SRI"
                                >
                                    <i className="ri-error-warning-fill"></i>
                                </Button>
                            )}

                            <Button
                                color="info"
                                size="sm"
                                onClick={() => onRequestCopy(rowData)}
                                title="Copiar Guía"
                            >
                                <i className="ri-file-copy-2-fill"></i>
                            </Button>
                        </div>
                    );
                },
            },
        ],
        [onRequestCopy, handleRetry, optimisticEnqueuedIds, retryingIds]
    );

    const totalPages = useMemo(() => {
        const total = pagination?.total ?? 0;
        return Math.ceil(total / pageSize) || 0;
    }, [pagination?.total, pageSize]);

    return (
        <div className="page-content position-relative">
            <Container fluid>
                <BreadCrumb title="Lista de Guías de Remisión" pageTitle="Inicio" />
                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardHeader className="d-flex justify-content-between align-items-center">
                                <h4 className="card-title mb-0">Listado de Guías de Remisión</h4>
                                <Button color="primary" onClick={() => window.location.href = '/remission-guide'}>
                                    <i className="ri-add-line align-middle me-1"></i> Nueva Guía
                                </Button>
                            </CardHeader>
                            <CardBody>
                                <TableContainer
                                    columns={columns}
                                    data={(waybillList || []).filter(Boolean)}
                                    isGlobalFilter={true}
                                    customPageSize={pageSize}
                                    divClass="table-responsive table-card mb-1"
                                    tableClass="align-middle table-nowrap"
                                    theadClass="table-light text-muted"
                                    SearchPlaceholder="Buscar guía..."
                                    onSearch={(value: string) => {
                                        if (value !== searchTerm) {
                                            debouncedSearch(value);
                                        }
                                    }}
                                    isCustomPagination={true}
                                    pageCount={totalPages}
                                    totalCount={pagination.total}
                                    pageIndex={currentPage - 1}
                                    onPageChange={(page: number) => setCurrentPage(page + 1)}
                                    loading={isLoading}
                                    additionalFilters={
                                        <div className="d-flex gap-2">
                                            {statusFilterComponent}
                                            {dateFilterComponent}
                                        </div>
                                    }
                                />
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
                <PdfViewerModal
                    isOpen={pdfModalOpen}
                    toggle={() => setPdfModalOpen(!pdfModalOpen)}
                    pdfUrl={pdfUrl}
                />

                <RemissionGuideDetailModal
                    isOpen={detailModalOpen}
                    toggle={() => setDetailModalOpen(false)}
                    guide={null}
                />

                <SriErrorModal
                    isOpen={errorModalOpen}
                    toggle={() => setErrorModalOpen(false)}
                    status={currentErrorStatus}
                    errors={currentErrors}
                />
                
                <CopyConfirmModal
                    show={copyConfirmOpen}
                    setOpen={setCopyConfirmOpen}
                    itemToCopy={guideToCopy}
                    onConfirm={(guide: any) => { handleCopyGuide(guide); setGuideToCopy(null); }}
                    type="guide"
                />
            </Container>
        </div>
    );
};

export default RemissionGuideList;
